import { useState } from 'react';
import { TRANSACTION_TYPES } from '../../data/constants';

/**
 * Gets transaction type details based on transaction type
 * @param {string} transactionType
 * @returns {{ sourceTypeId: 6 | 13, typeId: 2 | 3 | 120 }} Object containing transaction source type ID and transaction type ID
 */
const getTransactionTypeDetails = (transactionType) => {
  switch (transactionType) {
    case TRANSACTION_TYPES.ISSUE_TO_LINE:
      return { sourceTypeId: 6, typeId: 120 };
    case TRANSACTION_TYPES.DIRECT_ORG_TRANSFER:
      return { sourceTypeId: 13, typeId: 3 };
    case TRANSACTION_TYPES.SUB_INVENTORY_TRANSFER:
      return { sourceTypeId: 13, typeId: 2 };
    default:
      throw new Error(`Unknown transaction type: ${transactionType}`);
  }
};

/**
 * Custom hook to manage transaction items.
 * @returns {Local.UseTransactionItemsReturn}
 */
export const useTransactionItems = () => {
  const [transactionItems, setTransactionItems] = useState(/** @type {Local.TransactionItem[]} */ ([]));

  /**
   * Adds a specified quantity of an item to the transaction items.
   * @param {Pella.ProductionRequestAPI.GetSubinventoryInfoByPlantOutput} item
   * @param {number} qty
   * @param {number} index
   * @param {string} transactionType
   * @param {number} orgId
   * @param {number} [dispositionId]
   * @param {string | null} [transferSubinventory]
   * @param {number | null} [transferLocatorId]
   */
  const addQuantity = (
    item,
    qty,
    index,
    transactionType,
    orgId,
    dispositionId = undefined,
    transferSubinventory = null,
    transferLocatorId = null,
  ) => {
    const { sourceTypeId: transactionSourceTypeId, typeId: transactionTypeId } =
      getTransactionTypeDetails(transactionType);

    setTransactionItems((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        item: item.item,
        transactionQuantity: qty * -1,
        inventoryItemId: item.inventoryItemId,
        organizationId: orgId,
        sourceHeaderId: index + 1,
        sourceLineId: index + 1,
        transactionSourceTypeId,
        transactionTypeId,
        transactionUom: item.unitOfIssueCode,
        unitOfIssueCode: item.unitOfIssueCode,
        subinventoryCode: item.subinventoryCode,
        transferOrganization: orgId,
        locatorId: item.locatorId ?? null,
        sourceRowIndex: index,
        transactionSourceId: dispositionId,
        transferLocatorId,
        transferSubinventory,
        lotNumber: item.lotNumber,
        reasonId: null,
        shipToLocationId: null,
        distributionAccountId: null,
      },
    ]);
  };

  /**
   * Adds a full lot of the specified item to the transaction items.
   * @param {Pella.ProductionRequestAPI.GetSubinventoryInfoByPlantOutput} item
   * @param {number} qty
   * @param {number} index
   * @param {string} transactionType
   * @param {number} orgId
   * @param {number} [dispositionId]
   * @param {string | null} [transferSubinventory]
   * @param {number | null} [transferLocatorId]
   */
  const addFullLot = (
    item,
    qty,
    index,
    transactionType,
    orgId,
    dispositionId = undefined,
    transferSubinventory = null,
    transferLocatorId = null,
  ) => {
    const { sourceTypeId: transactionSourceTypeId, typeId: transactionTypeId } =
      getTransactionTypeDetails(transactionType);

    setTransactionItems((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        item: item.item,
        transactionQuantity: qty * -1,
        inventoryItemId: item.inventoryItemId,
        organizationId: orgId,
        sourceHeaderId: prev?.length ? prev.length + 1 : 1,
        sourceLineId: prev?.length ? prev.length + 1 : 1,
        transactionSourceTypeId,
        transactionTypeId,
        transactionUom: item.unitOfIssueCode,
        unitOfIssueCode: item.unitOfIssueCode,
        subinventoryCode: item.subinventoryCode,
        transferOrganization: orgId,
        locatorId: item.locatorId,
        sourceRowIndex: index,
        transactionSourceId: dispositionId,
        transferLocatorId,
        transferSubinventory,
        lotNumber: item.lotNumber,
        reasonId: null,
        shipToLocationId: null,
        distributionAccountId: null,
      },
    ]);
  };

  /**
   * handles the removal of an item from the transaction array
   * @param {string} id
   */
  const deleteItem = (id) => setTransactionItems((prev) => prev.filter((i) => i.id !== id));
  const reset = () => setTransactionItems([]);

  return { transactionItems, addQuantity, addFullLot, deleteItem, reset };
};
